import api from "lib/api";
import { useCallback } from "react";
import produce from "immer";
import { Task } from "records/Task";
import { State } from "hooks/usePagedData";
import { createErrorAlert } from "lib/api/errors";
import { useDispatch } from "react-redux";
import { createAlertAction } from "data/app/appActions";
import { AxiosError } from "axios";
import { createAlert } from "records/Alert";
import { useTranslation } from "react-i18next";

export function useTaskList(state: State, patchState: (data: State) => void) {
	const reduxDispatch = useDispatch();
	const { t } = useTranslation();

	/**
	 * Toggling the task read/unread
	 *
	 * @param id task id
	 * @param read current read status
	 */
	const handleReadUnread = useCallback(
		async (id: string, read: boolean) => {
			try {
				await api.workflow.updateReadMarker(id, !read);

				// If the request was successful we just manually patch our state instead
				// of loading everything again from the API
				patchState(
					produce(state, draft => {
						const index = draft.results.findIndex(
							(task: Task) => task.id === id
						);
						draft.results[index].taskRead =
							!draft.results[index].taskRead;
					})
				);
			} catch (error) {
				const e = error as AxiosError;
				reduxDispatch(
					createAlertAction(
						createErrorAlert(e, "readUnreadError", {
							id,
						})
					)
				);
			}
		},
		[patchState, reduxDispatch, state]
	);

	/**
	 * Changing the task priority
	 *
	 * @param id task id
	 * @param priority task priority: high, low, normal
	 */
	const changePriority = useCallback(
		async (id: string, priority: Task["priority"]) => {
			try {
				await api.workflow.updateTaskPriority(id, priority);
				patchState(
					produce(state, draft => {
						const index = draft.results.findIndex(
							(task: Task) => task.id === id
						);
						draft.results[index].priority = priority;
					})
				);
			} catch (error) {
				const e = error as AxiosError;
				reduxDispatch(
					createAlertAction(
						createErrorAlert(e, "priorityError", {
							id,
						})
					)
				);
			}
		},
		[patchState, reduxDispatch, state]
	);

	/**
	 * Updating state to the correct offset
	 *
	 * @param id task id
	 */
	const produceOffsetPatch = useCallback(
		(id: string | string[]) =>
			produce(state, draft => {
				if (Array.isArray(id)) {
					const indexes: number[] = [];
					for (const [index, task] of draft.results.entries()) {
						id.includes(task.id) && indexes.push(index);
					}
					for (let i = 0; i < indexes.length; i++) {
						draft.results.splice(indexes[i] - i, 1);
					}
					const newOffset = draft.offset - indexes.length;
					draft.offset = newOffset;
				} else {
					const index = draft.results.findIndex(
						(task: Task) => task.id === id
					);
					draft.results.splice(index, 1);
					const newOffset = draft.offset - 1;
					draft.offset = newOffset;
				}
			}),
		[state]
	);

	/**
	 * Unassigning a task off the tasks page
	 *
	 * @param id task id
	 */
	const unassignTask = useCallback(
		async (id: string) => {
			try {
				await api.workflow.unassignTask(id);
				patchState(produceOffsetPatch(id));
			} catch (error) {
				const e = error as AxiosError;

				reduxDispatch(
					createAlertAction(
						createErrorAlert(e, "unassignError", {
							id,
						})
					)
				);
			}
		},
		[patchState, produceOffsetPatch, reduxDispatch]
	);

	/**
	 * Deleting an activity off the tasks page
	 *
	 * @param id task id
	 */
	const deleteActivity = useCallback(
		async (id: string) => {
			try {
				await api.workflow.deleteActivity(id, { handleErrors: true });
				patchState(produceOffsetPatch(id));
			} catch (error) {
				const e = error as AxiosError;

				reduxDispatch(
					createAlertAction(
						createErrorAlert(e, "deleteActivityError", {
							id,
						})
					)
				);
			}
		},
		[patchState, produceOffsetPatch, reduxDispatch]
	);

	/**
	 * Claiming a task off the tasks page
	 *
	 * @param id task id
	 */
	const claimTask = useCallback(
		async (id: string | string[]) => {
			try {
				if (Array.isArray(id)) {
					const errors = await api.workflow.claimMultipleTasks(id);
					const errorCount = Object.keys(errors).length;
					const successCount = id.length - errorCount;

					reduxDispatch(
						createAlertAction(
							createAlert({
								title: t("alerts.claimTasks.title", {
									successCount,
								}),
								status:
									successCount !== 0 ? "success" : "error",
								timeout: 3000,
							})
						)
					);

					if (errorCount > 0) {
						Object.entries(errors).forEach(([key, value]) => {
							reduxDispatch(
								createAlertAction(
									createAlert({
										title: t("alerts.claimTaskError.title"),
										status: "error",
										description: (key +
											" " +
											value) as string,
									})
								)
							);
						});
					}
				} else {
					await api.workflow.claimTask(id);
					reduxDispatch(
						createAlertAction(
							createAlert({
								title: t("alerts.claimTask.title"),
								status: "success",
								timeout: 3000,
							})
						)
					);
				}
				patchState(produceOffsetPatch(id));
			} catch (error) {
				const e = error as AxiosError;

				reduxDispatch(
					createAlertAction(createErrorAlert(e, "claimTaskError"))
				);
			}
		},
		[patchState, produceOffsetPatch, reduxDispatch, t]
	);

	return {
		handleReadUnread,
		changePriority,
		unassignTask,
		deleteActivity,
		claimTask,
	};
}
